import { ApiClient } from '@/port/provider/ApiClient'
import Logger from '@renanmalato/logger-plus'

// ------------------------------- //
//  User Profile Interface         //
// ------------------------------- //

export interface UserProfile {
  email: string
  name: string
  givenName?: string
  familyName?: string
  picture?: string
}

// ------------------------------- //
//  User Vertical Interface        //
// ------------------------------- //

export interface UserVertical {
  chains: number[]
  sectors: string[]
  categories: string[]
  focusPropertyTypes: string[]
}

// ------------------------------- //
//  User Preferences Interface     //
// ------------------------------- //

export interface UserPreferences {
  dashboard?: DashboardPreferences
  visual?: VisualPreferences
  toolbar?: ToolbarPreferences
}

export interface DashboardPreferences {
  defaultView?: string
  sort?: Record<string, any>
}

// ------------------------------- //
//  Visual Preferences             //
// ------------------------------- //

export type GridDensity = 'DEFAULT' | 'TIGHT'
export type GridSortClick = 'SINGLE' | 'STACK'

export interface VisualPreferences {
  grid?: { density?: GridDensity; sortClick?: GridSortClick }
  typography?: { fontBoost?: number }
}

// ------------------------------- //
//  Toolbar Preferences           //
// ------------------------------- //

export interface ToolbarPreferences {
  findProperties?: { bottomLeft?: string }
}

// ------------------------------- //
//  User with Preferences          //
// ------------------------------- //

export interface UserListingRef {
  propertyId: string
  assignedAt: string
  notes?: string | null
}

export interface UserWithPreferences {
  profile: UserProfile
  vertical: UserVertical
  preferences: UserPreferences
  listings?: UserListingRef[]
  prospects?: UserListingRef[]
}

const PATH = '/crm';

// ------------------------------- //
//  User Profile Service           //
// ------------------------------- //

class UserProfileService {
  private cache: Map<string, UserProfile> = new Map()
  private loadingPromises: Map<string, Promise<UserProfile>> = new Map()

  // ------------------------------- //
  //  Get Single User Profile        //
  // ------------------------------- //

  async getUserProfile(email: string): Promise<UserProfile> {
    // Check cache first
    if (this.cache.has(email)) {
      Logger.info('👤 User profile from cache', 'UserProfileService', { email })
      return this.cache.get(email)!
    }

    // Check if already loading
    if (this.loadingPromises.has(email)) {
      Logger.info('⏳ User profile already loading', 'UserProfileService', { email })
      return await this.loadingPromises.get(email)!
    }

    // Start loading
    const loadingPromise = this.fetchUserProfile(email)
    this.loadingPromises.set(email, loadingPromise)

    try {
      const profile = await loadingPromise
      this.cache.set(email, profile)
      this.loadingPromises.delete(email)
      
      return profile
    } catch (error) {
      this.loadingPromises.delete(email)
      Logger.error('❌ Failed to load user profile', 'UserProfileService', { email, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Get Multiple User Profiles     //
  // ------------------------------- //

  async getUserProfiles(emails: string[]): Promise<Map<string, UserProfile>> {
    const result = new Map<string, UserProfile>()
    const emailsToFetch: string[] = []

    // Check cache for existing profiles
    for (const email of emails) {
      if (this.cache.has(email)) {
        result.set(email, this.cache.get(email)!)
        Logger.info('👤 User profile from cache (batch)', 'UserProfileService', { email })
      } else {
        emailsToFetch.push(email)
      }
    }

    // Fetch missing profiles in batch
    if (emailsToFetch.length > 0) {
      try {
        Logger.info('🔄 Fetching user profiles in batch', 'UserProfileService', { emails: emailsToFetch })
        const batchProfiles = await this.fetchUserProfilesBatch(emailsToFetch)
        
        // Cache and add to result
        for (const [email, profile] of batchProfiles) {
          this.cache.set(email, profile)
          result.set(email, profile)
        }
        
        Logger.info('✅ Batch user profiles loaded', 'UserProfileService', { count: batchProfiles.size })
      } catch (error) {
        Logger.error('❌ Failed to load batch user profiles', 'UserProfileService', { emails: emailsToFetch, error })
        throw error
      }
    }

    return result
  }

  // ------------------------------- //
  //  Preload Users                  //
  // ------------------------------- //

  async preloadUsers(emails: string[]): Promise<void> {
    const emailsToPreload = emails.filter(email => !this.cache.has(email) && !this.loadingPromises.has(email))
    
    if (emailsToPreload.length === 0) {
      Logger.info('ℹ️ All users already cached or loading', 'UserProfileService', { emails })
      return
    }

    try {
      Logger.info('🔄 Preloading user profiles', 'UserProfileService', { emails: emailsToPreload })
      await this.getUserProfiles(emailsToPreload)
      Logger.info('✅ User profiles preloaded', 'UserProfileService', { count: emailsToPreload.length })
    } catch (error) {
      Logger.error('❌ Failed to preload user profiles', 'UserProfileService', { emails: emailsToPreload, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Clear Cache                    //
  // ------------------------------- //

  clearCache(): void {
    this.cache.clear()
    this.loadingPromises.clear()
    Logger.info('🗑️ User profile cache cleared', 'UserProfileService')
  }

  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    MONGODB USER PREFERENCES METHODS               //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  // ------------------------------- //
  //  Get Current User Full          //
  // ------------------------------- //

  async getCurrentUserFull(): Promise<UserWithPreferences> {
    try {
      const response = await ApiClient.get<UserWithPreferences>(`${PATH}/users/profile/me/full`)
      
      if (!response) {
        throw new Error('No user data found')
      }

      Logger.info('✅ Fetched current user full data', 'UserProfileService', { email: response.profile.email, categoriesCount: response.vertical.categories.length })
      return response
    } catch (error) {
      Logger.error('❌ Failed to fetch current user full data', 'UserProfileService', { error })
      throw error
    }
  }

  // ------------------------------- //
  //  Get Current User Vertical      //
  // ------------------------------- //

  async getCurrentUserVertical(): Promise<UserVertical> {
    try {
      const response = await ApiClient.get<UserVertical>(`${PATH}/users/profile/me/vertical`)
      
      if (!response) {
        throw new Error('No user vertical found')
      }

      Logger.info('✅ Fetched current user vertical', 'UserProfileService', { chainsCount: response.chains.length, categoriesCount: response.categories.length })
      return response
    } catch (error) {
      Logger.error('❌ Failed to fetch current user vertical', 'UserProfileService', { error })
      throw error
    }
  }

  // ------------------------------- //
  //  Update Current User Vertical   //
  // ------------------------------- //

  async updateCurrentUserVertical(vertical: UserVertical): Promise<void> {
    try {
      await ApiClient.put(`${PATH}/users/profile/me/vertical`, vertical)
      Logger.success('✅ Updated current user vertical', 'UserProfileService', { chainsCount: vertical.chains.length, categoriesCount: vertical.categories.length })
    } catch (error) {
      Logger.error('❌ Failed to update current user vertical', 'UserProfileService', { error })
      throw error
    }
  }

  // ------------------------------- //
  //  Update Current User Categories //
  // ------------------------------- //

  async updateCurrentUserCategories(categories: string[]): Promise<void> {
    try {
      await ApiClient.put(`${PATH}/users/profile/me/vertical/categories`, categories)
      Logger.success('✅ Updated current user categories', 'UserProfileService', { categoriesCount: categories.length })
    } catch (error) {
      Logger.error('❌ Failed to update current user categories', 'UserProfileService', { error })
      throw error
    }
  }

  // ------------------------------- //
  //  Add Sector                     //
  // ------------------------------- //

  async addSector(sectorName: string): Promise<void> {
    try {
      await ApiClient.post(`${PATH}/users/profile/me/vertical/sectors/${encodeURIComponent(sectorName)}`, {})
      Logger.success('Added sector', 'UserProfileService', { sectorName })
    } catch (error) {
      Logger.error('Failed to add sector', 'UserProfileService', { sectorName, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Remove Sector                  //
  // ------------------------------- //

  async removeSector(sectorName: string): Promise<void> {
    try {
      await ApiClient.delete(`${PATH}/users/profile/me/vertical/sectors/${encodeURIComponent(sectorName)}`)
      Logger.success('Removed sector', 'UserProfileService', { sectorName })
    } catch (error) {
      Logger.error('Failed to remove sector', 'UserProfileService', { sectorName, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Add Focus Property Type        //
  // ------------------------------- //

  async addFocusPropertyType(propertyType: string): Promise<void> {
    try {
      await ApiClient.post(`${PATH}/users/profile/me/vertical/focus-property-types/${encodeURIComponent(propertyType)}`, {})
      Logger.success('Added focus property type', 'UserProfileService', { propertyType })
    } catch (error) {
      Logger.error('Failed to add focus property type', 'UserProfileService', { propertyType, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Remove Focus Property Type     //
  // ------------------------------- //

  async removeFocusPropertyType(propertyType: string): Promise<void> {
    try {
      await ApiClient.delete(`${PATH}/users/profile/me/vertical/focus-property-types/${encodeURIComponent(propertyType)}`)
      Logger.success('Removed focus property type', 'UserProfileService', { propertyType })
    } catch (error) {
      Logger.error('Failed to remove focus property type', 'UserProfileService', { propertyType, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Add Tracked Chain              //
  // ------------------------------- //

  async addTrackedChain(chainId: number): Promise<void> {
    try {
      await ApiClient.post(`${PATH}/users/profile/me/vertical/chains/${chainId}`, {})
      Logger.success('✅ Added tracked chain', 'UserProfileService', { chainId })
    } catch (error) {
      Logger.error('❌ Failed to add tracked chain', 'UserProfileService', { chainId, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Remove Tracked Chain           //
  // ------------------------------- //

  async removeTrackedChain(chainId: number): Promise<void> {
    try {
      await ApiClient.delete(`${PATH}/users/profile/me/vertical/chains/${chainId}`)
      Logger.success('✅ Removed tracked chain', 'UserProfileService', { chainId })
    } catch (error) {
      Logger.error('❌ Failed to remove tracked chain', 'UserProfileService', { chainId, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Change My Password             //
  // ------------------------------- //

  async changeMyPassword(newPassword: string): Promise<boolean> {
    try {
      await ApiClient.put(`${PATH}/users/profile/me/password`, { newPassword })
      Logger.success('Password changed', 'UserProfileService')
      return true
    } catch (error) {
      Logger.error('Failed to change password', 'UserProfileService', { error })
      return false
    }
  }

  // ------------------------------- //
  //  Upload My Profile Photo        //
  // ------------------------------- //

  async uploadMyPhoto(file: File): Promise<string | null> {
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await ApiClient.postFormData<{ url: string }>(`${PATH}/users/profile/me/photo`, formData)
      Logger.success('Photo uploaded', 'UserProfileService')
      return response?.url || null
    } catch (error) {
      Logger.error('Failed to upload photo', 'UserProfileService', { error })
      return null
    }
  }

  // ------------------------------- //
  //  Update Current User Preferences //
  // ------------------------------- //

  async updateCurrentUserPreferences(preferences: UserPreferences): Promise<UserPreferences> {
    try {
      const user = await ApiClient.put<{ preferences?: UserPreferences }>(`${PATH}/users/profile/me/preferences`, preferences as any)
      Logger.success('✅ Updated current user preferences', 'UserProfileService')
      return user?.preferences ?? preferences
    } catch (error) {
      Logger.error('❌ Failed to update current user preferences', 'UserProfileService', { error })
      throw error
    }
  }

  // ------------------------------------------------- //
  //                                                   //
  //                                                   //
  //    ADMIN METHODS                                  //
  //                                                   //
  //                                                   //
  // ------------------------------------------------- //

  // ------------------------------- //
  //  Get All Users (Admin)          //
  // ------------------------------- //

  async getAllUsers(): Promise<UserProfile[]> {
    try {
      const response = await ApiClient.get<UserProfile[]>(`${PATH}/users/profiles/all`)
      Logger.info('✅ Fetched all users', 'UserProfileService', { count: response?.length || 0 })
      return response || []
    } catch (error) {
      Logger.error('❌ Failed to fetch all users', 'UserProfileService', { error })
      throw error
    }
  }

  // ------------------------------- //
  //  Get Any User Full (Admin)      //
  // ------------------------------- //

  async getUserFull(email: string): Promise<UserWithPreferences> {
    try {
      const response = await ApiClient.get<UserWithPreferences>(`${PATH}/users/profile/${encodeURIComponent(email)}/full`)
      Logger.info('✅ Fetched user full data (admin)', 'UserProfileService', { email })
      return response
    } catch (error) {
      Logger.error('❌ Failed to fetch user full data', 'UserProfileService', { email, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Update Any User Categories (Admin) //
  // ------------------------------- //

  async updateUserCategories(email: string, categories: string[]): Promise<void> {
    try {
      await ApiClient.put(`${PATH}/users/profile/${encodeURIComponent(email)}/vertical/categories`, categories)
      Logger.success('✅ Updated user categories (admin)', 'UserProfileService', { email, categoriesCount: categories.length })
    } catch (error) {
      Logger.error('❌ Failed to update user categories', 'UserProfileService', { email, error })
      throw error
    }
  }

  // ------------------------------- //
  //  Private Methods                //
  // ------------------------------- //

  private async fetchUserProfile(email: string): Promise<UserProfile> {
    try {
      const response = await ApiClient.get<UserProfile>(`${PATH}/users/profile/${encodeURIComponent(email)}`)
      
      if (!response) {
        throw new Error(`No user profile found for ${email}`)
      }

      return response
    } catch (error) {
      Logger.error('❌ API error fetching user profile', 'UserProfileService', { email, error })
      throw new Error(`Failed to fetch user profile for ${email}: ${error}`)
    }
  }

  private async fetchUserProfilesBatch(emails: string[]): Promise<Map<string, UserProfile>> {
    try {
      const emailsParam = emails.join(',')
      const response = await ApiClient.get<Record<string, UserProfile>>(`${PATH}/users/profiles?emails=${encodeURIComponent(emailsParam)}`)
      
      if (!response) {
        throw new Error('No user profiles found')
      }

      const result = new Map<string, UserProfile>()
      for (const [email, profile] of Object.entries(response)) {
        result.set(email, profile)
      }

      return result
    } catch (error) {
      Logger.error('❌ API error fetching batch user profiles', 'UserProfileService', { emails, error })
      throw new Error(`Failed to fetch user profiles: ${error}`)
    }
  }
}

// ------------------------------- //
//  Export Singleton               //
// ------------------------------- //

export const userProfileService = new UserProfileService()
export default userProfileService
